<template>
  <div class="space-y-8">
    <!-- Header Section -->
    <div class="flex justify-between items-center">
      <div>
        <h3 class="text-lg font-medium text-heading">
          {{ $t('AI_SETTINGS.DATA_COLLECTION.TITLE') }}
        </h3>
        <p class="text-sm text-body-light">
          {{ $t('AI_SETTINGS.DATA_COLLECTION.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <!-- Main Configuration Section -->
    <div class="bg-background rounded-lg border border-border p-6">
      <div class="space-y-6">
        <!-- Enable Data Collection Toggle -->
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-md font-medium text-heading">
              {{ $t('AI_SETTINGS.DATA_COLLECTION.ENABLE_TOGGLE.TITLE') }}
            </h4>
            <p class="text-sm text-body-light">
              {{ $t('AI_SETTINGS.DATA_COLLECTION.ENABLE_TOGGLE.DESCRIPTION') }}
            </p>
          </div>
          <woot-switch
            :model-value="config.is_enabled"
            @input="onToggleEnabled"
          />
        </div>

        <!-- Auto Stop Chatbot Toggle -->
        <div class="flex items-center justify-between">
          <div>
            <h4 class="text-md font-medium text-heading">
              {{ $t('AI_SETTINGS.DATA_COLLECTION.AUTO_STOP_TOGGLE.TITLE') }}
            </h4>
            <p class="text-sm text-body-light">
              {{ $t('AI_SETTINGS.DATA_COLLECTION.AUTO_STOP_TOGGLE.DESCRIPTION') }}
            </p>
          </div>
          <woot-switch
            :model-value="config.auto_stop_chatbot"
            @input="onToggleAutoStop"
          />
        </div>
      </div>
    </div>

    <!-- Fields Management Section -->
    <div class="bg-background rounded-lg border border-border p-6">
      <div class="flex justify-between items-center mb-6">
        <h4 class="text-md font-medium text-heading">
          {{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.TITLE') }}
        </h4>
        <woot-button
          color-scheme="primary"
          icon="add"
          @click="showFieldModal = true"
        >
          {{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.ADD_FIELD') }}
        </woot-button>
      </div>

      <!-- Fields List -->
      <div v-if="fields.length" class="space-y-4">
        <div
          v-for="field in fields"
          :key="field.id"
          class="flex items-center justify-between p-4 border border-border rounded-lg"
        >
          <div class="flex-1">
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2">
                <woot-switch
                  :model-value="field.is_enabled"
                  @input="val => { field.is_enabled = val; updateField(field) }"
                />
                <span class="text-sm font-medium text-heading">
                  {{ field.display_name }}
                </span>
              </div>
              <span class="text-xs text-body-light">({{ field.field_name }})</span>
              <span
                v-if="field.is_required"
                class="px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full"
              >
                {{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.REQUIRED') }}
              </span>
            </div>
            <p v-if="field.description" class="text-sm text-body-light mt-1">
              {{ field.description }}
            </p>
          </div>
          
          <div class="flex items-center gap-2">
            <woot-button
              variant="clear"
              size="small"
              icon="edit"
              @click="editField(field)"
            />
            <woot-button
              variant="clear"
              size="small"
              icon="delete"
              @click="deleteField(field)"
            />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-8 text-body-light"
      >
        {{ $t('AI_SETTINGS.DATA_COLLECTION.FIELDS.EMPTY_STATE') }}
      </div>
    </div>

    <!-- Google Sheets Sync Section (placed below Fields Management) -->
    <GoogleSheetsSyncCard />

    <!-- Field Modal -->
    <FieldModal
      v-if="showFieldModal"
      :show="showFieldModal"
      :field="editingField"
      @close="closeFieldModal"
      @save="saveField"
    />
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables'
import FieldModal from './FieldModal.vue'
import GoogleSheetsSyncCard from './GoogleSheetsSyncCard.vue'

export default {
  name: 'DataCollectionTab',
  
  components: {
    FieldModal,
    GoogleSheetsSyncCard,
  },
  
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['refresh'],
  
  data() {
    return {
      config: {
        is_enabled: false,
        auto_stop_chatbot: false
      },
      fields: [],
      showFieldModal: false,
      editingField: null,
      isLocalLoading: false
    }
  },
  
  mounted() {
    this.loadDataCollectionConfig()
  },
  
  methods: {
    async loadDataCollectionConfig() {
      try {
        this.isLocalLoading = true
        const accountId = this.$store.getters.getCurrentAccountId
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/data_collection`)
        
        if (response.data) {
          this.config = {
            is_enabled: response.data.is_enabled || false,
            auto_stop_chatbot: response.data.auto_stop_chatbot || false
          }
          this.fields = response.data.fields || []
        }
      } catch (error) {
        console.error('Error loading data collection config:', error)
        if (error.response && error.response.status !== 404) {
          useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.LOAD_FAILED'), 'error')
        }
      } finally {
        this.isLocalLoading = false
      }
    },
    
    async updateConfig() {
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        await window.axios.put(`/api/v1/accounts/${accountId}/data_collection`, this.config)
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.CONFIG_UPDATED'), 'success')
      } catch (error) {
        console.error('Error updating config:', error)
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.CONFIG_UPDATE_FAILED'), 'error')
        // Revert changes
        this.loadDataCollectionConfig()
      }
    },

    onToggleEnabled(val) {
      this.config.is_enabled = val
      this.updateConfig()
    },

    onToggleAutoStop(val) {
      this.config.auto_stop_chatbot = val
      this.updateConfig()
    },
    
    async updateField(field) {
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        await window.axios.put(`/api/v1/accounts/${accountId}/data_collection/fields/${field.id}`, {
          field_name: field.field_name,
          display_name: field.display_name,
          description: field.description,
          is_required: field.is_required,
          is_enabled: field.is_enabled,
          order_index: field.order_index
        })
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.FIELD_UPDATED'), 'success')
      } catch (error) {
        console.error('Error updating field:', error)
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.FIELD_UPDATE_FAILED'), 'error')
        // Revert changes
        this.loadDataCollectionConfig()
      }
    },
    
    editField(field) {
      this.editingField = { ...field }
      this.showFieldModal = true
    },
    
    async deleteField(field) {
      if (!window.confirm(this.$t('AI_SETTINGS.DATA_COLLECTION.DELETE.CONFIRM_MESSAGE'))) return
      
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        await window.axios.delete(`/api/v1/accounts/${accountId}/data_collection/fields/${field.id}`)
        
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.FIELD_DELETED'), 'success')
        this.loadDataCollectionConfig()
      } catch (error) {
        console.error('Error deleting field:', error)
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.FIELD_DELETE_FAILED'), 'error')
      }
    },
    
    closeFieldModal() {
      this.showFieldModal = false
      this.editingField = null
    },
    
    async saveField(fieldData) {
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        
        if (this.editingField) {
          // Update existing field
          await window.axios.put(`/api/v1/accounts/${accountId}/data_collection/fields/${this.editingField.id}`, fieldData)
          useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.FIELD_UPDATED'), 'success')
        } else {
          // Create new field
          await window.axios.post(`/api/v1/accounts/${accountId}/data_collection/fields`, fieldData)
          useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.SUCCESS.FIELD_CREATED'), 'success')
        }
        
        this.closeFieldModal()
        this.loadDataCollectionConfig()
      } catch (error) {
        console.error('Error saving field:', error)
        useAlert(this.$t('AI_SETTINGS.DATA_COLLECTION.ERRORS.FIELD_SAVE_FAILED'), 'error')
      }
    }
  }
}
</script>
